import { BaseCardConfig, CardType } from './card-model';
import type { DateRangeExpression } from './date-range-expression.model';
import * as Highcharts from 'highcharts';

/**
 * Chart type definitions
 * Note: Table is a separate CardType, not a ChartType
 */
export enum ChartType {
	Line = 'line',
	Bar = 'bar',
	Donut = 'donut',
	Scatter = 'scatter',
	Column = 'column',
	Area = 'area',
	Number = 'number',
}

/**
 * Aggregation type definitions
 */
export enum Aggregation {
	SUM = 'SUM',
	AVG = 'AVG',
	MIN = 'MIN',
	MAX = 'MAX',
	COUNT = 'COUNT',
	COUNT_DISTINCT = 'COUNT_DISTINCT',
}

const AGGREGATIONS = new Set<string>(Object.values(Aggregation));

export function isAggregation(value: unknown): value is Aggregation {
	return typeof value === 'string' && AGGREGATIONS.has(value);
}

export enum ComparisonPeriod {
	None = 'NONE',
	PreviousPeriod = 'PREVIOUS_PERIOD',
	PreviousYear = 'PREVIOUS_YEAR',
}

/**
 * Time dimension source definitions
 */
export enum TimeDimensionSource {
	Global = 'global',
	Chart = 'chart',
}

/**
 * Data type definitions
 */
export enum DataType {
	Number = 'number',
	String = 'string',
	Time = 'time',
}

/**
 * GraphQL `IntervalInput` — values for `TimeDimensionsInput.granularity` on `getCardData`
 * and for chart {@link TimeDimensionConfig.granularity}.
 */
export enum Granularity {
	Day = 'DAY',
	Week = 'WEEK',
	Month = 'MONTH',
	Quarter = 'QUARTER',
	Year = 'YEAR',
}

const GRANULARITY_VALUES = new Set<string>(Object.values(Granularity));

/**
 * Coerces an unknown value to {@link Granularity}. Only exact `IntervalInput` strings match;
 * anything else becomes {@link Granularity.Day}.
 */
export function parseGranularity(raw: unknown): Granularity {
	if (typeof raw !== 'string') {
		return Granularity.Day;
	}
	const t = raw.trim();
	if (GRANULARITY_VALUES.has(t)) {
		return t as Granularity;
	}
	return Granularity.Day;
}

/**
 * Data source type definitions
 */
export enum DataSourceType {
	GoogleAnalytics4 = 'googleAnalytics4',
	GoogleBusinessProfile = 'googleBusinessProfile',
	GoogleSearchConsole = 'googleSearchConsole',
	GoogleAds = 'googleAds',
	FacebookAds = 'facebookAds',
	InstagramAds = 'instagramAds',
	LinkedInAds = 'linkedInAds',
	TikTokAds = 'tiktokAds',
	TwitterAds = 'twitterAds',
	MicrosoftAds = 'microsoftAds',
}

/**
 * Source configuration for card data queries
 * Matches actual card data structure: sources[{ authId, dataSource }]
 */
export interface SourceConfig {
	authId: string /** Authentication ID for the data source */;
	dataSource: DataSourceType | string /** Data source type */;
}

/**
 * Comparison configuration
 * Matches actual card data: comparison: { enabled, period: "PREVIOUS_PERIOD" | "PREVIOUS_YEAR" }
 */
export interface ComparisonConfig {
	enabled: boolean;
	period?: ComparisonPeriod | string /** Comparison period (uppercase) */;
	start?: string /** Only required when period is 'RANGE' */;
	end?: string /** Only required when period is 'RANGE' */;
}

/**
 * Metric configuration
 * Matches actual card data structure with id, name, label, dataType, aggregation
 */
export interface MetricConfig {
	id: string /** Unique identifier (format: metric_[6_char_hex]) */;
	name: string /** Fully qualified metric name (e.g., "googleBusinessProfile.mapImpressions") */;
	label: string /** Human-readable display name */;
	dataType: DataType | string /** Data type: "number", "string", "time" */;
	aggregation?: Aggregation;

	/** Optional fields for advanced metric types */
	type?:
		| 'metric'
		| 'prediction'
		| 'target'
		| 'custom'
		| 'custom-complex' /** Metric type */;
	displayType?: 'line' | 'bar' | 'area';
	cumulative?: boolean /** Display as cumulative (defaults to false) */;
	baseMetric?: string /** Required for prediction and target types */;
	source?: string /** Data source (only for custom-complex metrics) */;
	formula?: string /** Formula for custom metrics */;
	description?: string /** Description of the calculation */;
}

/**
 * Dimension configuration
 * Matches actual card data structure with id, name, dataType
 */
export interface DimensionConfig {
	id: string /** Unique identifier (format: dimension_[6_char_hex]) */;
	name: string /** Dimension field name (e.g., "date", "locationName") */;
	dataType: DataType | string /** Data type: "time", "string", "number" */;

	/** Optional extended configuration */
	label?: string /** Human-readable display name */;
	format?: FormatConfig /** Formatting configuration */;
	sortable?: boolean /** Whether column is sortable (tables) */;
	filterable?: boolean /** Whether column is filterable (tables) */;
	hidden?: boolean /** Whether column is hidden (tables) */;
}

/**
 * Time dimension configuration (singular object, not array)
 * Matches actual card data: timeDimension: { dimension, granularity, timeDimensionSource, dateRange? }
 */
export interface TimeDimensionConfig {
	dimension: string /** Time field identifier (e.g., "date") */;
	granularity:
		| Granularity
		| string /** Prefer {@link Granularity} (`IntervalInput` strings). */;
	timeDimensionSource:
		| TimeDimensionSource
		| string /** Source: "global" or "chart" */;
	dateRange?: DateRangeExpression /** e.g. `["last30days"]` or `["6monthsAgo", "today"]`; required when timeDimensionSource is "chart" */;
	dataType?: string /** Optional data type */;
}

/** Cube-style filter operators (aligned with `jepto-data-filter`). */
export type FilterOperator =
	| 'equals'
	| 'notEquals'
	| 'contains'
	| 'notContains'
	| 'startsWith'
	| 'endsWith'
	| 'gt'
	| 'gte'
	| 'lt'
	| 'lte'
	| 'set'
	| 'notSet'
	| 'inDateRange'
	| 'notInDateRange'
	| 'beforeDate'
	| 'afterDate';

const FILTER_OPERATORS = new Set<FilterOperator>([
	'equals',
	'notEquals',
	'contains',
	'notContains',
	'startsWith',
	'endsWith',
	'gt',
	'gte',
	'lt',
	'lte',
	'set',
	'notSet',
	'inDateRange',
	'notInDateRange',
	'beforeDate',
	'afterDate',
]);

export function isFilterOperator(value: string): value is FilterOperator {
	return FILTER_OPERATORS.has(value as FilterOperator);
}

/**
 * Filter configuration
 */
export interface FilterConfig {
	member: string /** Fully qualified field to filter (e.g., "googleBusinessProfile.locationId") */;
	operator: FilterOperator /** Filter operator */;
	values: (string | number)[] /** Values to filter by */;
}

/**
 * Order configuration (sorting)
 */
export type OrderConfig = Record<string, 'asc' | 'desc'>;

/**
 * Format configuration for metrics/dimensions
 */
export interface FormatConfig {
	type?: 'decimal' | 'currency' | 'percentage' | 'number' | 'string';
	precision?: number;
	currency?: string;
	/** Percentage input scale: fraction (0.0523 → 5.23%) vs already percent. */
	displayScale?: 'fraction' | 'percent';
	prefix?: string;
	suffix?: string;
	grouping?: boolean;
	compact?: boolean;
	pattern?: string;
}

/**
 * Visualization configuration.
 *
 * Persisted as a `Highcharts.Options` payload (the chart components run
 * `Highcharts.merge(defaults, cfg.visualization)` at render time). The
 * framework's palette pipeline reads/writes `colors` for series cycling.
 */
export type VisualizationConfig = Highcharts.Options;

/**
 * Presentation settings that are not part of Highcharts options.
 * These settings are chart-type specific UI rendering controls.
 */
export interface ChartPresentationConfig {
	number?: NumberPresentationOptions;
}

/**
 * Chart data configuration
 * Matches actual card data.data structure with metrics[], dimensions[], timeDimension{}, filters[], comparison{}
 */
export interface ChartDataConfig {
	/** Metrics to query and display */
	metrics?: MetricConfig[];

	/** Dimensions for grouping data */
	dimensions?: DimensionConfig[];

	/** Time dimension configuration (singular object) */
	timeDimension?: TimeDimensionConfig;

	/** Filter conditions */
	filters?: FilterConfig[];

	/** Sort order */
	order?: OrderConfig;

	/** Comparison configuration */
	comparison?: ComparisonConfig;
}

/**
 * Palette settings for color customization
 */
export interface PaletteConfig {
	selectedPaletteId?: string;
	/**
	 * When true, the card follows the report’s theme palette (`getCard.paletteId` absent); no card-level `paletteId` on save.
	 */
	inheritsReportThemePalette?: boolean;
	/**
	 * When true (default), the card uses a predefined palette selection and color editors are read-only.
	 * When false, the user is authoring a new palette (name + editable colors).
	 */
	useDefaultPalette?: boolean;
	/** Display name for a new custom palette when {@link useDefaultPalette} is false. */
	customPaletteName?: string;
	/** Override colors for light theme. */
	customLight?: string[];
	/** Override colors for dark theme. */
	customDark?: string[];
}

/**
 * Whether persisted {@link PaletteConfig.customLight} / {@link PaletteConfig.customDark}
 * should affect chart rendering.
 *
 * - Custom palette authoring (`useDefaultPalette === false`): always active.
 * - Named palette with edited swatches (`useDefaultPalette === true` and non-empty custom arrays): active.
 * - When {@link PaletteConfig.inheritsReportThemePalette} is true: never active (report theme wins).
 * - When `useDefaultPalette` is omitted (legacy cards), non-empty custom arrays still apply.
 */
export function isPaletteCustomOverridesActive(
	palette?: Pick<
		PaletteConfig,
		| 'inheritsReportThemePalette'
		| 'useDefaultPalette'
		| 'customLight'
		| 'customDark'
	>
): boolean {
	if (!palette) return false;
	if (palette.inheritsReportThemePalette === true) return false;
	if (palette.useDefaultPalette === false) return true;
	if (palette.useDefaultPalette === true) {
		return (
			(Array.isArray(palette.customLight) &&
				palette.customLight.length > 0) ||
			(Array.isArray(palette.customDark) && palette.customDark.length > 0)
		);
	}
	return (
		(Array.isArray(palette.customLight) &&
			palette.customLight.length > 0) ||
		(Array.isArray(palette.customDark) && palette.customDark.length > 0)
	);
}

/**
 * Base chart card configuration with common properties for all chart types
 * Matches actual card data structure
 */
export interface ChartCardConfig extends BaseCardConfig {
	cardType: CardType.Chart /** Card type identifier */;
	chartType: ChartType | string /** Chart type */;

	/** Data sources array - matches actual: sources[{ authId, dataSource }] */
	sources?: SourceConfig[];

	/** Data configuration (query definition) */
	data?: ChartDataConfig;

	/** Visualization configuration (display styling) */
	visualization?: VisualizationConfig;

	/** Non-Highcharts presentation configuration by chart type. */
	presentation?: ChartPresentationConfig;

	/** Color palette configuration */
	palette?: PaletteConfig;

	/**
	 * When true, chart data should be refetched (e.g. after preview date range change).
	 * Editor/runtime flag; omit when persisted shape does not include it.
	 */
	needsDataRefresh?: boolean;
}

/**
 * Options specific to Number Chart cards.
 */
export interface NumberPresentationOptions {
	/** Number display format */
	format?: {
		prefix?: string /** Prefix before the number (e.g. "$") */;
		suffix?: string /** Suffix after the number (e.g. "%") */;
		precision?: number /** Number of decimal places */;
		compact?: boolean /** Use compact number notation (e.g. 1.2M) */;
	};
	showSparkline?: boolean /** Show mini trend sparkline below the value */;
	lineWidth?: number /** Sparkline stroke width (Chart tab, typically 1–6) */;
	sparklineHeight?: number /** Sparkline height in pixels */;
	valueSize?:
		| 'sm'
		| 'md'
		| 'lg'
		| 'xl' /** Font size preset for primary value */;
}

/**
 * Line chart specific configuration
 */
export interface LineChartConfig extends ChartCardConfig {
	chartType: ChartType.Line;
}

/**
 * Bar chart specific configuration
 */
export interface BarChartConfig extends ChartCardConfig {
	chartType: ChartType.Bar;
}

/**
 * Donut chart specific configuration
 */
export interface DonutChartConfig extends ChartCardConfig {
	chartType: ChartType.Donut;
}

/**
 * Area chart specific configuration
 */
export interface AreaChartConfig extends ChartCardConfig {
	chartType: ChartType.Area;
}

/**
 * Column chart specific configuration
 */
export interface ColumnChartConfig extends ChartCardConfig {
	chartType: ChartType.Column;
}

/**
 * Scatter chart specific configuration
 */
export interface ScatterChartConfig extends ChartCardConfig {
	chartType: ChartType.Scatter;
}

/**
 * Number chart specific configuration
 */
export interface NumberChartConfig extends ChartCardConfig {
	chartType: ChartType.Number;
}
